import {
  BaseQueryFn,
  EndpointBuilder,
  QueryDefinition,
  MutationDefinition,
} from '@reduxjs/toolkit/query';

import type { IClientFavoriteContact } from 'interfaces/ClientFavoriteContact.interface';
import type { ICollectionApiResponse } from 'services/api/api.types';
import type {
  IClientFavoriteContactCollectionArgs,
  IClientFavoriteContactCollectionResponse,
  IClientFavoriteContactItemArgs,
  IClientFavoriteContactPostArgs,
  IClientFavoriteContactPutArgs,
  IClientFavoriteContactDeleteArgs,
} from 'services/clientFavoriteContact/clientFavoriteContact.types';
import type { ISimpleFilter } from 'hooks/useFilters.hook';
import {
  serializeFilters,
  serializeFlatObject,
} from 'services/helper/getPaginationParams.helper';
import { transformCollectionResponse } from 'services/teamHeroApi.helpers';
import { teamHeroApi } from 'services/teamHeroApi.service';

export const getClientFavoriteContacts = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): QueryDefinition<
  IClientFavoriteContactCollectionArgs,
  BaseQueryFn,
  string,
  IClientFavoriteContactCollectionResponse
> =>
  build.query<
    IClientFavoriteContactCollectionResponse,
    IClientFavoriteContactCollectionArgs
  >({
    providesTags: ['ClientFavoriteContact'],
    query: (queryArg: IClientFavoriteContactCollectionArgs) => {
      const paginationParamObject = {
        page: queryArg.page,
        itemsPerPage: queryArg.itemsPerPage,
        pagination: queryArg.pagination,
        ...queryArg.order,
      };
      const paginationQuery = serializeFlatObject(paginationParamObject, '?');
      const filterQuery = serializeFilters(queryArg.filters);
      return {
        url: `api/client_favorite_contacts${paginationQuery}${filterQuery}`,
      };
    },
    transformResponse: (
      response: ICollectionApiResponse<IClientFavoriteContact>
    ): IClientFavoriteContactCollectionResponse =>
      transformCollectionResponse<IClientFavoriteContact>(response),
  });

export const getClientFavoriteContactItem = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): QueryDefinition<
  IClientFavoriteContactItemArgs,
  BaseQueryFn,
  string,
  IClientFavoriteContact
> =>
  build.query<IClientFavoriteContact, IClientFavoriteContactItemArgs>({
    providesTags: ['ClientFavoriteContact'],
    query: (queryArg: IClientFavoriteContactItemArgs) => ({
      url: `api/client_favorite_contacts/${queryArg.id}`,
    }),
  });

export const postClientFavoriteContactItem = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<
  IClientFavoriteContactPostArgs,
  BaseQueryFn,
  string,
  IClientFavoriteContact
> =>
  build.mutation<IClientFavoriteContact, IClientFavoriteContactPostArgs>({
    invalidatesTags: ['ClientFavoriteContact'],
    query: (queryArg) => ({
      url: `api/client_favorite_contacts`,
      method: 'POST',
      body: queryArg.body,
    }),
  });

export const putClientFavoriteContactItem = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<
  IClientFavoriteContactPutArgs,
  BaseQueryFn,
  string,
  IClientFavoriteContact
> =>
  build.mutation<IClientFavoriteContact, IClientFavoriteContactPutArgs>({
    invalidatesTags: (_result, _error, args) =>
      args.skipInvalidateTags ? [] : ['ClientFavoriteContact'],
    query: (queryArg) => ({
      url: `api/client_favorite_contacts/${queryArg.id}`,
      method: 'PUT',
      body: queryArg.body,
    }),
  });

export const putClientFavoriteContactOptimisticItem = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<
  IClientFavoriteContactPutArgs,
  BaseQueryFn,
  string,
  IClientFavoriteContact
> =>
  build.mutation<IClientFavoriteContact, IClientFavoriteContactPutArgs>({
    invalidatesTags: (_result, _error, args) =>
      args.skipInvalidateTags ? [] : ['ClientFavoriteContact'],
    query: (queryArg) => ({
      url: `api/client_favorite_contacts/${queryArg.id}`,
      method: 'PUT',
      body: queryArg.body,
    }),
    async onQueryStarted(
      { id, ...patch },
      { dispatch, queryFulfilled, getState }
    ) {
      const state = getState();
      const clientFavoriteQueries = teamHeroApi.util.selectInvalidatedBy(
        state,
        ['ClientFavoriteContact']
      );

      const queriesToUpdateAdd = clientFavoriteQueries.filter((query) => {
        if (!query) return false;
        const originalArgs =
          query.originalArgs as IClientFavoriteContactCollectionArgs;
        return (
          originalArgs.filters &&
          originalArgs.filters.some(
            (filter: ISimpleFilter) =>
              filter.key === 'status' && filter.value === patch.body.status
          )
        );
      });

      const queriesToUpdateRemove = clientFavoriteQueries.filter((query) => {
        if (!query) return false;
        const originalArgs =
          query.originalArgs as IClientFavoriteContactCollectionArgs;
        return (
          originalArgs.filters &&
          originalArgs.filters.some(
            (filter: ISimpleFilter) =>
              filter.key === 'status' && filter.value !== patch.body.status
          )
        );
      });

      queriesToUpdateRemove.map((query) => {
        const originalArgs = query?.originalArgs;
        return dispatch(
          teamHeroApi.util.updateQueryData(
            'getClientFavoriteContacts',
            {
              filters: originalArgs?.filters || [],
              ...originalArgs,
            },
            (draft) => {
              const index = draft.items.findIndex((item) => item.id === id);
              if (index !== -1) {
                // save a copy of the item before removing it
                const itemToBeMoved = draft.items[index];
                draft.items.splice(index, 1);

                // add the item back with the updated data
                // add to caches
                queriesToUpdateAdd.map((query) => {
                  const originalArgs =
                    query?.originalArgs as IClientFavoriteContactCollectionArgs;
                  return dispatch(
                    teamHeroApi.util.updateQueryData(
                      'getClientFavoriteContacts',
                      {
                        filters: originalArgs?.filters || [],
                        ...originalArgs,
                      },
                      (draft) => {
                        // check, if item is already in the collection
                        const existingItemIndex = draft.items.findIndex(
                          (item) => item.id === itemToBeMoved.id
                        );
                        // only add if it is not already in the collection
                        if (existingItemIndex === -1) {
                          // add the item to the collection
                          draft.items.push({
                            ...itemToBeMoved,
                            ...draft.items,
                          });
                        }
                      }
                    )
                  );
                });
              }
            }
          )
        );
      });
      try {
        await queryFulfilled;
      } catch {
        dispatch(teamHeroApi.util.invalidateTags(['ClientFavoriteContact']));
      }
    },
  });

export const deleteClientFavoriteContactItem = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<
  IClientFavoriteContactDeleteArgs,
  BaseQueryFn,
  string,
  IClientFavoriteContact
> =>
  build.mutation<IClientFavoriteContact, IClientFavoriteContactDeleteArgs>({
    invalidatesTags: ['ClientFavoriteContact'],
    query: (queryArg) => ({
      url: `api/client_favorite_contacts/${queryArg.id}`,
      method: 'DELETE',
    }),
  });
